﻿/**
 * Multi-value picker: one clickable text showing what is picked, opening a modal list of
 * checkboxes with a search box and All/None. Extracted from mbasico's filter sentence, whose
 * reason for existing it keeps — a handful of multi-word labels is enough to push an inline
 * control off the line, so the picking happens in a dialog and only the result stays on it.
 *
 * The options come from a {@link ValuesSource}, which is the whole of what varies between
 * callers: a list already in hand, a list worth fetching only once the dialog opens, or a set
 * too large to hold at all, where every term is a request.
 */

import {JSX, ReactNode, useEffect, useMemo, useState} from "react";
import {sortByText, textMatches} from "../helpers/textSearch";
import Button from "../Buttons/Button";
import Dialog from "../Dialog/Dialog";
import Checkbox from "./Checkbox";

export interface ValueOption {
	value: string;
	label: string;
	/**
	 * Ordering band, default 0. The list sorts by this first and alphabetically within it, so a
	 * caller can sink a whole class of option below the rest without giving up the alphabet —
	 * entries the current filter excludes, say, which must stay pickable but should not sit
	 * between the ones that are live.
	 */
	group?: number;
}

/** Where the dialog's options come from. */
export type ValuesSource =
	/** The complete set, already in hand. The search box filters it locally. */
	| {kind: "list"; options: readonly ValueOption[]}
	/** The complete set, fetched once when the dialog opens. The search box filters it locally. */
	| {kind: "load"; load: (signal: AbortSignal) => Promise<ValueOption[]>}
	/** No complete set: every term is a request, and the source owns the matching. */
	| {kind: "search"; search: (query: string, signal: AbortSignal) => Promise<ValueOption[]>; minChars?: number; debounceMs?: number};

export interface ValuesPickerMessages {
	title?: string;
	search?: string;
	all?: string;
	none?: string;
	ok?: string;
	cancel?: string;
	/** Nothing to list. */
	empty?: string;
	/** Shown in "search" mode until enough characters are typed. `{min}` is substituted. Left
	 *  unset the picker picks the singular or plural wording from the count itself. */
	minChars?: string;
	/** Trigger text when nothing is picked. */
	placeholder?: string;
}

/** Defaults filled in, except `minChars`: its wording depends on a count only the dialog knows. */
type ResolvedMessages = Required<Omit<ValuesPickerMessages, "minChars">> & Pick<ValuesPickerMessages, "minChars">;

const DEFAULT_MESSAGES: Required<Omit<ValuesPickerMessages, "minChars">> = {
	title: "Select values",
	search: "Search",
	all: "All",
	none: "None",
	ok: "OK",
	cancel: "Cancel",
	empty: "No values",
	placeholder: "Select"
};

const DEFAULT_DEBOUNCE_MS = 350;
const DEFAULT_MIN_CHARS = 3;

export interface ValuesPickerProps {
	source: ValuesSource;
	value: readonly ValueOption[];
	onChange: (picked: ValueOption[]) => void;
	/** False turns the list into a single pick — ticking one unticks the other. */
	multiple?: boolean;
	/** Accessible name for the trigger and the search box. */
	label: string;
	/** Id for the trigger, so a wrapping <label htmlFor> points at it. */
	id?: string;
	/** Trigger text. Defaults to the picked labels, comma-separated. */
	text?: string;
	/** Extra class on the trigger, for callers that style it in place (the filter sentence). */
	triggerClassName?: string;
	disabled?: boolean;
	messages?: ValuesPickerMessages;
}

/** Ticking a box. Multi-value adds to what is there; single-value replaces it — a checkbox is
 *  still the right control there, ticking one is just what unticks the other. Ticking a value
 *  that is already picked always removes it. */
export function togglePicked(picked: ReadonlyMap<string, string>, option: ValueOption, multiple: boolean): Map<string, string> {
	if (picked.has(option.value)) {
		const next = new Map(picked);
		next.delete(option.value);
		return next;
	}
	const next = multiple ? new Map(picked) : new Map<string, string>();
	next.set(option.value, option.label);
	return next;
}

/** What the list shows: the options on offer plus every picked value, even one the current
 *  results don't carry (a new search term in lazy mode would otherwise take it off the screen
 *  with no way to untick it). Alphabetical within each {@link ValueOption.group} — a picked value
 *  keeps its place in the alphabet instead of jumping to the top, or the list reorders under the
 *  box just clicked. */
export function listedOptions(picked: ReadonlyMap<string, string>, options: readonly ValueOption[]): ValueOption[] {
	const extra: ValueOption[] = [...picked]
		.filter(([value]) => !options.some(o => o.value === value))
		.map(([value, label]) => ({value, label}));
	const sorted = sortByText<ValueOption>([...options, ...extra], o => o.label);
	// Stable, so the alphabetical order just established survives inside each band.
	return sorted.sort((a, b) => (a.group ?? 0) - (b.group ?? 0));
}

export default function ValuesPicker(props: ValuesPickerProps): JSX.Element {
	const {source, value, onChange, multiple = true, label, id, text, triggerClassName, disabled, messages} = props;
	// Rendered here rather than through useDialog: a picker may well live inside another dialog,
	// and DialogProvider has a single slot — showing a second one would destroy its host. A plain
	// <Dialog> portals to <body> and showModal()s, so the browser's top layer stacks the two.
	const [open, setOpen] = useState(false);
	const caption = useMemo(() => text ?? value.map(o => o.label).join(", "), [text, value]);
	const msg = useMemo(() => ({...DEFAULT_MESSAGES, ...messages}), [messages]);

	const close = (picked: ValueOption[] | undefined): void => {
		setOpen(false);
		if (picked != null) {
			onChange(picked);
		}
	};

	return <>
		<button type={"button"} id={id} className={triggerClassName} aria-label={label} disabled={disabled}
		        title={caption === "" ? undefined : caption} onClick={() => setOpen(true)}>
			{/* The span is what gets clipped: an overflow:hidden flex item synthesizes its baseline
			    from the border box, which would drop the text off its host's baseline. */}
			<span>{caption === "" ? msg.placeholder : caption}</span>
		</button>
		{open && <Dialog title={msg.title} onClose={() => setOpen(false)}>
			<ValuesDialog source={source} value={value} multiple={multiple} label={label} messages={msg} close={close}/>
		</Dialog>}
	</>;
}

interface ValuesDialogProps {
	source: ValuesSource;
	value: readonly ValueOption[];
	multiple: boolean;
	label: string;
	messages: ResolvedMessages;
	close: (picked: ValueOption[] | undefined) => void;
}

function ValuesDialog({source, value, multiple, label, messages, close}: ValuesDialogProps): JSX.Element {
	const [picked, setPicked] = useState(() => new Map(value.map(o => [o.value, o.label])));
	const [search, setSearch] = useState("");
	const [fetched, setFetched] = useState<ValueOption[]>([]);
	const [loading, setLoading] = useState(false);

	const remote = source.kind === "search";
	const minChars = source.kind === "search" ? source.minChars ?? DEFAULT_MIN_CHARS : 0;
	const debounceMs = source.kind === "search" ? source.debounceMs ?? DEFAULT_DEBOUNCE_MS : 0;
	// Undefined outside search mode, so a keystroke that only filters locally never refetches.
	const term = remote ? search.trim() : undefined;
	const tooShort = term != null && term.length < minChars;

	const load = source.kind === "load" ? source.load : undefined;
	const searchFn = source.kind === "search" ? source.search : undefined;

	useEffect(() => {
		if (load == null && searchFn == null) {
			return;
		}
		if (tooShort) {
			setFetched([]);
			// A term shortened mid-flight lands here: without this the stale `loading` keeps
			// suppressing the "no values" message once the term is long enough again.
			setLoading(false);
			return;
		}
		const controller = new AbortController();
		setLoading(true);
		// The full list is only ever fetched once (term stays undefined); a search term waits out
		// the typing first.
		const timer = setTimeout(async () => {
			try {
				const result = searchFn != null
					? await searchFn(term ?? "", controller.signal)
					: await load!(controller.signal);
				if (!controller.signal.aborted) {
					setFetched(result);
				}
			} catch {
				// aborted or failed lookup — keep whatever is showing
			} finally {
				if (!controller.signal.aborted) {
					setLoading(false);
				}
			}
		}, term == null ? 0 : debounceMs);

		return () => {
			controller.abort();
			clearTimeout(timer);
		};
	}, [load, searchFn, term, tooShort, debounceMs]);

	const options: readonly ValueOption[] = source.kind === "list" ? source.options : fetched;
	// In search mode the source already matched the term; elsewhere the whole list is here. A
	// picked option stays listed whatever the term says — it has to be untickable — and taking it
	// from the full set here, rather than letting listedOptions re-synthesize it from the picked
	// map, is what keeps its `group`: the map holds labels only.
	const visible = remote ? options : options.filter(o => picked.has(o.value) || textMatches(o.label, search));
	const entries = listedOptions(picked, visible);

	const toggle = (option: ValueOption): void => setPicked(prev => togglePicked(prev, option, multiple));

	// Multi-value only: ticking twenty boxes by hand is what these are for. "All" takes what is
	// listed, so it follows the search term rather than ignoring it — and in lazy mode the listed
	// results are all there is to take.
	const pickAll = (): void => setPicked(prev => new Map([...prev, ...entries.map(o => [o.value, o.label] as const)]));

	// Mirror of "All": it drops what is listed, not picks made under a previous search term.
	const pickNone = (): void => setPicked(prev => {
		const next = new Map(prev);
		entries.forEach(o => next.delete(o.value));
		return next;
	});

	const accept = (): void => close([...picked].map(([optionValue, optionLabel]) => ({value: optionValue, label: optionLabel})));

	const message: ReactNode = tooShort
		? (messages.minChars ?? (minChars === 1 ? "Type at least {min} character" : "Type at least {min} characters"))
			.replace("{min}", String(minChars))
		: (!loading && entries.length === 0)
			? messages.empty
			: null;

	return <div className={"values-dialog"}>
		<input type={"search"} className={"values-dialog__search"} value={search} aria-label={label} autoFocus={true}
		       placeholder={messages.search}
		       onChange={e => setSearch(e.target.value)}/>

		{multiple &&
			<div className={"values-dialog__bulk"}>
				<Button small={true} disabled={entries.length === 0} onClick={pickAll}>{messages.all}</Button>
				<Button small={true} disabled={picked.size === 0 || entries.length === 0} onClick={pickNone}>{messages.none}</Button>
			</div>}

		<ul className={"values-dialog__list"}>
			{entries.map(option =>
				<li key={option.value}>
					<Checkbox name={option.value} label={option.label} checked={picked.has(option.value)}
					          onChange={() => toggle(option)}/>
				</li>)}
		</ul>

		{message != null && <p className={"values-dialog__message"}>{message}</p>}

		<div className={"values-dialog__footer"}>
			<Button onClick={() => close(undefined)}>{messages.cancel}</Button>
			{/* NOT kind="primary": that renders type="submit", which the host <form method="dialog">
			    would act on before the click handler runs. */}
			<Button kind={"confirm"} onClick={accept}>{messages.ok}</Button>
		</div>
	</div>;
}
